/**
 * Custom PieChartComponent - Child Class
 * Extends parent and overrides radius to 70%
 */
import { Component, AfterViewInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { PieChartComponent as ParentPieChartComponent } from '@shared-lib/chart-components/pie-chart/pie-chart.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';

@Component({
  selector: 'app-pie-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pie-chart.component.html',
  styleUrls: ['./pie-chart.component.scss']
})
export class PieChartComponent extends ParentPieChartComponent implements AfterViewInit, OnDestroy {

  private customChart!: echarts.ECharts;
  private customConfigService = inject(ConfigService);
  private customGlobalFilterService = inject(GlobalFilterService);

  constructor() {
    super();

    // Subscribe to filter changes
    this.customGlobalFilterService.filter$.subscribe((filters) => {
      if (Object.keys(filters).length !== 0) {
        this.loadCustomChart(filters);
      }
    });
  }

  override ngAfterViewInit(): void {
    this.loadCustomChart();
  }

  private loadCustomChart(filters?: any): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (!chartDom) return;

    if (!this.customChart) {
      this.customChart = echarts.init(chartDom);
    }

    this.customChart.showLoading();

    this.customConfigService.getConfig(this.data.apiUrl, filters).subscribe({
      next: (res) => {
        this.renderCustomChart(res?.data);
      },
      error: () => {
        this.customChart.hideLoading();
      }
    });
  }

  private renderCustomChart(chartData?: [{ name: string; value: number }]): void {
    const option = {
      title: {
        left: 'center'
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b} : {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        left: 'left',
      },
      series: [
        {
          name: this.data.title || '',
          type: 'pie',
          // OVERRIDE: Changed radius from '50%' to '70%'
          radius: '20%',
          data: chartData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };

    this.customChart.setOption(option);
    setTimeout(() => {
      this.customChart.hideLoading();
    }, 1000);

    window.addEventListener('resize', () => this.customChart?.resize());
  }

  override ngOnDestroy(): void {
    this.customChart?.dispose();
  }
}
